import {
  CalculatorInputs,
  CalculatorOutputs,
  EmployeeValueOutputs,
  SustainabilityOutputs,
} from '../types/calculator';
import {
  HOURS_PER_YEAR,
  MINUTES_PER_HOUR,
  FTE_HOURS_PER_YEAR,
  CO2_PER_CUP,
  WATER_PER_CUP,
  WASTE_PER_CUP,
  WASTE_AVOIDED_PER_CUP,
  ENERGY_PER_CUP,
  PLASTIC_PER_CUP,
  TREES_PER_CUP,
  CO2_PER_CAR_KM,
  KM_CONVERSION,
} from '../constants/defaults';

/**
 * Calculate all employee value metrics
 */
export const calculateEmployeeValue = (
  inputs: CalculatorInputs
): EmployeeValueOutputs => {
  const {
    employeeCount,
    avgSalary,
    breakDuration,
    cupsPerEmployee,
    officeDaysPerWeek,
    subsidyPercent,
  } = inputs;

  // Calculate wage rates
  const hourlyWage = avgSalary / HOURS_PER_YEAR;

  // Total cups and time saved per day
  const totalCupsPerDay = employeeCount * cupsPerEmployee;
  const timeSavedHours = (breakDuration * totalCupsPerDay) / MINUTES_PER_HOUR;

  // Productivity savings (daily): timeSaved × hourlyWage
  const productivityValue = timeSavedHours * hourlyWage;

  // FTE recovered: Annualized FTE = (timeSaved × working days per year) / 1976
  // Working days = officeDaysPerWeek × 48 weeks
  const workingDaysPerYear = officeDaysPerWeek * 48;
  const fteRecovered = (timeSavedHours * workingDaysPerYear) / FTE_HOURS_PER_YEAR;

  // Company investment (daily): subsidy amount per cup × total cups
  const companyInvestment = totalCupsPerDay * subsidyPercent;

  // Savings per cup: What employees save vs buying at a cafe at $5.50
  // Employees save the subsidy amount per cup (what the company pays for them)
  const savingsPerCup = subsidyPercent;

  // Employee savings (daily): totalCups × savingsPerCup
  const employeeSavings = totalCupsPerDay * savingsPerCup;

  return {
    timeSavedHours,
    productivityValue,
    fteRecovered,
    companyInvestment,
    savingsPerCup,
    employeeSavings,
  };
};

/**
 * Calculate all sustainability metrics
 */
export const calculateSustainability = (
  totalCupsPerDay: number
): SustainabilityOutputs => {
  const cupsSaved = totalCupsPerDay;
  const co2Avoided = totalCupsPerDay * CO2_PER_CUP;
  const energySaved = totalCupsPerDay * ENERGY_PER_CUP;
  const waterSaved = totalCupsPerDay * WATER_PER_CUP;
  const wasteDiverted = totalCupsPerDay * WASTE_PER_CUP;
  const wasteAvoided = totalCupsPerDay * WASTE_AVOIDED_PER_CUP;
  const plasticSaved = totalCupsPerDay * PLASTIC_PER_CUP;

  // Trees planted: totalCups × 0.00125
  const treesEquivalent = totalCupsPerDay * TREES_PER_CUP;

  // KMs driven offset: (co2Saved / 0.404) × 1.60934
  const carKmOffset = (co2Avoided / CO2_PER_CAR_KM) * KM_CONVERSION;

  return {
    cupsSaved,
    co2Avoided,
    energySaved,
    waterSaved,
    wasteDiverted,
    wasteAvoided,
    plasticSaved,
    treesEquivalent,
    carKmOffset,
  };
};

/**
 * Calculate all outputs from inputs
 */
export const calculateAll = (inputs: CalculatorInputs): CalculatorOutputs => {
  const totalCupsPerDay = inputs.employeeCount * inputs.cupsPerEmployee;
  const employeeValue = calculateEmployeeValue(inputs);
  const sustainability = calculateSustainability(totalCupsPerDay);

  return {
    employeeValue,
    sustainability,
    totalCupsPerDay,
  };
};

/**
 * Apply time period multiplier to a value
 */
export const applyMultiplier = (value: number, multiplier: number): number => {
  return value * multiplier;
};
